// Section 24.8: project(state, draft, seasons) runs the real phases in projection mode with the
// ExpectedValueRng and the stochastic phases skipped, holding the draft decision constant.
// Deterministic and side-effect free. The panel labels itself "assuming no events".
import { produce } from 'immer';
import { config, type Config } from '../config';
import { registryFor } from '../content';
import { SimError } from '../core/errors';
import { runSeason } from '../core/runner';
import { peopleSorted, settlementTrust } from '../core/queries';
import { sortedIds } from '../core/sorted';
import { RATION_RESOURCES, RESOURCES } from '../core/constants';
import type {
  Decision,
  GameState,
  ProjectedSeason,
  Projection,
  PersonId,
  PriorityGroupId,
  PromptAnswer,
  RationResource,
  Resource,
} from '../core/types';
import { phaseOrder } from '../phases/phase-order';
import { readContext } from './context';
import { emptyDecision } from './decision';
import { stewardAnswer } from './steward/answer';

export interface ProjectOptions {
  cfg?: Config;
}

function seasonDecision(state: GameState, draft: Decision, first: boolean): Decision {
  const eventChoices: Decision['eventChoices'] = {};
  for (const prompt of state.openPrompts)
    eventChoices[prompt.id] = draft.eventChoices[prompt.id] ?? stewardAnswer(state, prompt);
  // Assignments and policies are idempotent; one-off commands apply only in the first season.
  return first
    ? { ...draft, turn: state.turn, eventChoices }
    : {
        ...draft,
        turn: state.turn,
        eventChoices,
        builds: [],
        renames: {},
        savedPriorityOrders: null,
        myHousehold: null,
      };
}

function snapshot(state: GameState): ProjectedSeason {
  const report = state.history[state.history.length - 1];
  if (report === undefined) throw new SimError('invariant', 'projection produced no report');
  const stocks = {} as Record<Resource, number>;
  for (const r of RESOURCES) stocks[r] = state.stocks[r].amount;
  const strength: ProjectedSeason['strength'] = {};
  const health: Record<string, number> = {};
  for (const p of peopleSorted(state)) {
    strength[p.id] = p.strength;
    health[p.id] = p.health;
  }
  const personOutputs: Record<string, number> = {};
  const outputs: ProjectedSeason['outputs'] = {};
  for (const id of sortedIds(report.outputs)) {
    const o = report.outputs[id];
    if (o === undefined) continue;
    personOutputs[id] = o.amount;
    outputs[o.jobId] = (outputs[o.jobId] ?? 0) + o.amount;
  }
  const shortages: RationResource[] = RATION_RESOURCES.filter((r) =>
    report.entries.some((e) => e.kind === 'shortage' && e.resource === r),
  );
  return {
    turn: report.turn,
    year: report.year,
    season: report.season,
    stocks,
    seedReserved: state.seedReserved,
    flows: report.flows,
    strength,
    health,
    personOutputs,
    outputs,
    ...(report.rations === undefined ? {} : { rations: report.rations }),
    needs: report.needs,
    expectedDeaths: report.expectedDeaths,
    trust: settlementTrust(state),
    gini: report.gini.food,
    fragility: report.fragility,
    shortages,
  };
}

export function project(
  state: GameState,
  draft: Decision,
  seasons = 4,
  opts: ProjectOptions = {},
): Projection {
  if (state.phaseCursor !== null)
    throw new SimError('bad_status', 'cannot project while a season is paused', {
      turn: state.turn,
    });
  const cfg = opts.cfg ?? config;
  const out: ProjectedSeason[] = [];
  let s = state;
  for (let i = 0; i < seasons && (s.outcome === null || s.sandbox); i++) {
    const decision = seasonDecision(s, draft, i === 0);
    s = produce(s, (d) => {
      d.turn += 1;
      d.decisionLog.push(decision);
    });
    s = runSeason(s, {
      cfg,
      registry: registryFor(cfg),
      phases: phaseOrder,
      mode: 'projection',
      checkInvariants: false,
    });
    if (s.phaseCursor !== null) break;
    out.push(snapshot(s));
  }
  return { baseTurn: state.turn, assumptions: 'no-events', seasons: out };
}

/**
 * Section 17: who is in each priority group now (the Rations tab's editor shows the counts).
 * Runs the registry's `members` hooks over the committed state.
 */
export function priorityGroupMembers(
  state: GameState,
  opts: ProjectOptions = {},
): Record<PriorityGroupId, PersonId[]> {
  const ctx = readContext(state, opts.cfg ?? config, 'projection');
  const out = {} as Record<PriorityGroupId, PersonId[]>;
  for (const id of Object.keys(ctx.registry.priorityGroups) as PriorityGroupId[])
    out[id] = [...ctx.registry.priorityGroups[id].members(ctx)];
  return out;
}

/**
 * Section 17 and 24.8: the shortage dialog's live strip. Finishes the paused season in
 * projection mode with a candidate answer to the pending shortage prompt (the real rationing
 * phase applies it), then projects further seasons holding this season's logged decision
 * constant. `seasons[0]` is the season being resolved. Deterministic and side-effect free.
 */
export function projectShortage(
  state: GameState,
  answer: PromptAnswer,
  seasons = 2,
  opts: ProjectOptions = {},
): Projection {
  const prompt = state.pendingPrompts[0];
  if (state.phaseCursor === null || prompt === undefined)
    throw new SimError('bad_status', 'projectShortage needs a season paused on a prompt', {
      turn: state.turn,
    });
  const cfg = opts.cfg ?? config;
  const run = (s: GameState): GameState =>
    runSeason(s, {
      cfg,
      registry: registryFor(cfg),
      phases: phaseOrder,
      mode: 'projection',
      checkInvariants: false,
    });
  let s = run(
    produce(state, (d) => {
      d.promptAnswers[prompt.id] = answer;
    }),
  );
  const out: ProjectedSeason[] = [];
  if (s.phaseCursor !== null)
    return { baseTurn: state.turn, assumptions: 'no-events', seasons: out };
  out.push(snapshot(s));
  const logged = state.decisionLog[state.decisionLog.length - 1];
  const held: Decision = { ...(logged ?? emptyDecision(state)), promptAnswers: {} };
  for (let i = 1; i < seasons && (s.outcome === null || s.sandbox); i++) {
    const decision = seasonDecision(s, held, false);
    s = run(
      produce(s, (d) => {
        d.turn += 1;
        d.decisionLog.push(decision);
      }),
    );
    if (s.phaseCursor !== null) break;
    out.push(snapshot(s));
  }
  return { baseTurn: state.turn, assumptions: 'no-events', seasons: out };
}
